import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/_workforce/training")({
  beforeLoad: ({ context }) => guardRoute(context.me, "training"),
  component: TrainingLayout,
});

interface TrainingSection {
  label: string;
  to: string;
}

const SECTIONS: readonly TrainingSection[] = [
  { label: "My training", to: "/training" },
  { label: "Overview", to: "/training/dashboard" },
  /* The courses themselves. They were reachable only by typing the URL: the
     screen that writes the training everybody else is chased for was not on
     the strip at all. */
  { label: "Courses", to: "/training/courses" },
  { label: "Who owes training", to: "/training/roster" },
  { label: "Templates", to: "/training/templates" },
];

/** Longest path wins, so `/training` does not claim `/training/roster`. */
function activeSection(pathname: string): string {
  let active = "/training";
  for (const section of SECTIONS) {
    const onIt = pathname === section.to || pathname.startsWith(`${section.to}/`);
    if (onIt && section.to.length >= active.length) active = section.to;
  }
  return active;
}

/** Course-only users get no tab strip, since a bar of one is noise. Report readers get the sections
 * side by side. */
function TrainingLayout() {
  const { can } = usePolicy();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (!can("training:read")) return <Outlet />;

  return (
    <>
      <Tabs value={activeSection(pathname)}>
        {/* A line strip, quieter than a segmented track, so the page title
            under it stays the first thing read. */}
        <TabsList fit="wrap" variant="line" className="justify-start border-b">
          {SECTIONS.map((section) => (
            <TabsTrigger key={section.to} value={section.to} className="flex-none px-2.5" asChild>
              <Link to={section.to}>{section.label}</Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Outlet />
    </>
  );
}
